import type { VideoExports } from '../hooks/use-video-exports';

export function exportBlocked(exports: VideoExports, seconds: number): boolean {
  const allowance = exports.commerce?.entitlement;
  return !!exports.commerce && exports.commerce.status !== 'local' && (!allowance || !allowance.canRender
    || (allowance.remainingExports !== null && allowance.remainingExports < 1)
    || seconds > Math.min(allowance.maxVideoSeconds, allowance.remainingSeconds ?? Infinity));
}

/** Keep the render gate and its visible explanation on the same conditions. */
export function exportBlockingReasons(exports: VideoExports, seconds: number, editing: {
  error?: string; playing?: boolean; previewReady?: boolean; previewOnly?: boolean; proFeatures?: boolean;
} = {}): string[] {
  const reasons: string[] = [];
  if (editing.error) reasons.push(editing.error);
  if (editing.playing) reasons.push('Pause playback before rendering.');
  if (editing.previewReady === false) reasons.push('Waiting for the preview to finish updating. Rendering becomes available when it is ready.');
  if (editing.previewOnly) reasons.push('This map style supports preview only. Choose another map style in Composition to render a video.');
  if (editing.proFeatures) reasons.push('This composition uses PRO settings. Review your account access or reset PRO settings above.');
  if (exports.submitting) reasons.push('Submitting your video export. Please wait.');
  else if (exports.busy) reasons.push('A video is rendering on this computer. Wait for it to finish or cancel it below.');
  if (exports.available !== true && !(exports.authRequired && exports.localSignIn)) {
    reasons.push(exports.authRequired ? 'Sign in using the link above to enable rendering.'
      : exports.available === null ? 'Connecting to the video service. Rendering becomes available when connected.'
      : exports.destinations?.destination === 'remote' ? exports.destinations.connection.message
      : exports.error || 'The video service is unavailable. Reconnecting automatically.');
  }
  if (exportBlocked(exports, seconds)) {
    const commerce = exports.commerce!;
    const allowance = commerce.entitlement;
    reasons.push(commerce.status === 'loading' ? 'Checking your account access. Please wait.'
      : commerce.status === 'signed-out' ? 'Sign in to your account to enable rendering.'
      : commerce.status === 'error' ? 'Account access could not be checked. Review your account and try again.'
      : !allowance?.canRender ? 'Your account does not currently have export access. Review your account.'
      : allowance.remainingExports !== null && allowance.remainingExports < 1 ? 'No video exports remain on your account. Review your account.'
      : `This export exceeds your account’s available video length (${Math.min(allowance.maxVideoSeconds, allowance.remainingSeconds ?? Infinity)} seconds). Shorten the video or review your account.`);
  }
  return reasons;
}
